const post = {
  meta: {
    slug: "5-red-flags-loan-broker",
    title: "5 Red Flags When Choosing a Business Loan Broker",
    excerpt:
      "A good broker will save you tens of thousands of dollars. A bad one will cost you the same. Here's how to tell them apart in the first five minutes.",
    date: "2025-12-05",
    readTime: "5 min read",
    category: "Playbook",
    image:
      "https://images.pexels.com/photos/33866469/pexels-photo-33866469.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  body: `
## Why the broker choice matters

A broker's job is simple: shop your file across the market, negotiate on your behalf, and translate the fine print into plain English before you sign. But because the industry pays brokers on commission at close, incentives can get bent. A bent broker can push you into the wrong product for a bigger payday — and it happens more than the industry likes to admit.

Here are the five clearest signals a broker isn't working for you.

## 1. They ask for an upfront fee

**The rule:** legitimate business finance brokers are paid by the lender at close. Not by you, not before, and not "just to lock in your rate."

If someone asks for an "application fee," "processing fee," "underwriting fee," or a "commitment deposit" before a single dollar is funded — walk away. This isn't a rate lock. This is a fee for nothing.

The one legitimate exception: **SBA loans** occasionally involve a "packaging fee" charged by a licensed loan packager, disclosed on Form 159 at closing. Even then, it's not paid up front and it's not paid to a broker.

## 2. They only quote one product

If you tell them you need $150,000 and they immediately say "MCA is your best option" — before pulling a statement, checking credit, or asking a single question about your business — they aren't shopping the market. They're feeding you into the highest-commission product they sell.

**The right first response** to "I need $150,000" is a question, not an answer. What's the money for? How's your credit? How long have you been in business? What's your last 4 months of revenue?

## 3. They can't (or won't) explain the total cost

Ask any broker: "What is the total dollar amount I will repay?"

A good broker will answer without hesitation. They'll cite the payback amount, the effective APR, the daily or monthly payment, and any prepayment discount. A bad broker will say things like "it depends" or "we'll figure that out at closing" or, worse, "focus on the factor rate, not APR — APR is misleading."

**APR isn't misleading.** APR is the one number that lets you compare a 12-month advance to a 5-year loan honestly. A broker who steers you away from APR is steering you away from the truth.

## 4. They "stack" without disclosing it

"Stacking" is when a broker places you into multiple advances on top of each other — often with different lenders — without disclosing that the combined daily debit will strain your cash flow.

A single revenue advance can be a healthy bridge. Three stacked advances is a slow-motion cash-flow crisis. It's also the fastest way to make a broker's monthly commission target.

**How to spot it:** ask "will this new advance be on top of anything I already have?" and "will the combined daily debit be under X% of my average daily deposits?" If they can't or won't answer clearly, they're not thinking about your business — they're thinking about their draw.

## 5. They pressure you to sign the same day

Business loan documents are legally binding personal guarantees. A good broker will send them over, walk you through every clause, and then step back. A bad broker will call, WhatsApp, and email until you sign, framing every hour of delay as a "rate risk" or "funding window closing."

Rates move. Real windows exist. But a rate that will genuinely evaporate in 24 hours is rare, and a lender that structures itself around 24-hour pressure is usually the wrong lender.

**The rule:** you should be able to say "I need 48 hours to review with my accountant" without your broker getting agitated. If they get agitated, that tells you everything.

## The one green flag that outweighs all others

The best test of a broker isn't a red flag to avoid — it's a green one to look for.

**They tell you when you shouldn't take the money.**

A broker who says "based on what I'm seeing, I don't think you should borrow right now" — or "you should try your bank first before we talk again" — is a broker who thinks past the next commission. That's the person you want on your file.

At Steady Path Funding, we've walked away from more deals in the last year than we've closed. Not because we don't want the work, but because the wrong deal is worse than no deal — for you *and* for us. That's the whole model.
`,
};

export default post;
